import { useRef, useState } from 'react';
import { Button, Input, Tooltip, message } from 'antd';
import { AttachmentIcon, CloseIcon, FileXlsxIcon } from '../../assets/icons';
import { DOC_ACCEPT, DOC_FORMATS, type Attachment, type PlanMode } from '../../features/aiPlanner/plannerApi';

interface Props {
  mode: PlanMode;
  engagementName: string;
  /** text to start with (the engagement's saved description, plan mode only) */
  initialText?: string;
  attachments: Attachment[];
  onAttach: (file: File) => Promise<unknown>;
  onDetach: (name: string) => Promise<unknown>;
  onContinue: (description: string) => void;
  loading: boolean;
}

const COPY: Record<PlanMode, { title: string; help: string; placeholder: string; button: string }> = {
  plan: {
    title: 'Describe the engagement',
    help: 'Write it the way you would explain it to a colleague: what the program is, where and when it happens, '
      + 'who will come, roughly how many people, what the leader will do, the money available, and anything already decided. '
      + 'The more you write here, the better the plan.',
    placeholder: 'Example: Teachers Day felicitation on 5 September at the Government Senior Secondary School hall in Behror. '
      + 'We want to honour about 40 teachers from 15 government schools with a shawl and a certificate signed by the MLA. '
      + 'MLA sahab will attend from 11 am to 1 pm. Around 200 people expected. Tea and snacks after. Budget about 1.5 lakh…',
    button: 'Make the plan',
  },
  add_program: {
    title: 'Describe the program you want to add',
    help: 'Tell us what the new program is and how it fits into the existing engagement: what happens, when in the day, '
      + 'who is involved, and anything already decided. The AI will ask you two or three short questions, then propose '
      + 'the new tasks and any changes to the existing ones.',
    placeholder: 'Example: We also want a short award ceremony for 10 meritorious students in the same programme. '
      + 'Each gets a certificate and Rs 2,000 from the MLA. Their parents should be invited too…',
    button: 'Continue',
  },
  remove_program: {
    title: 'Describe the program you want to remove',
    help: 'Tell us which part of the engagement is being dropped and why, and whether anything replaces it. '
      + 'The AI will propose which tasks to remove and which to change. Tasks already completed or in progress are never removed.',
    placeholder: 'Example: The cultural program by students is cancelled because the school has exams that week. '
      + 'The rest of the felicitation stays as it is…',
    button: 'Propose the changes',
  },
};

const fmtSize = (n: number) => (n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

/** Step 1 — one detailed description + optional files. */
export default function DescribeStep({
  mode, engagementName, initialText, attachments, onAttach, onDetach, onContinue, loading,
}: Props) {
  const c = COPY[mode];
  const prefilled = !!(initialText ?? '').trim();
  const [text, setText] = useState((initialText ?? '').trim());
  const [uploading, setUploading] = useState<string[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const ok = text.trim().length >= 20;

  const pick = async (files: FileList | null) => {
    if (!files?.length) return;
    for (const f of Array.from(files)) {
      setUploading((u) => [...u, f.name]);
      try { await onAttach(f); }
      catch (e) { message.error(`${f.name}: ${(e as { data?: { detail?: string } })?.data?.detail ?? 'could not be uploaded'}`); }
      finally { setUploading((u) => u.filter((n) => n !== f.name)); }
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="flex flex-col gap-4 py-1">
      <div>
        <h3 className="text-base font-semibold text-ink">{c.title}</h3>
        <p className="mt-1 text-sm leading-[22px] text-ink-secondary">{c.help}</p>
      </div>

      <Input.TextArea
        autoFocus value={text} onChange={(e) => setText(e.target.value)}
        placeholder={c.placeholder} autoSize={{ minRows: 8, maxRows: 16 }}
        className="!text-sm !leading-[22px]" disabled={loading}
      />

      {prefilled && (
        <p className="m-0 -mt-1 text-sm leading-[21px] text-[#DC2626]">
          This text was filled in from the engagement's saved description. Before you continue, please check that every
          detail is correct and up to date — the date and time, the place, how many people, what the leader will do, the
          money available, who is in charge — and add anything that is missing. The plan is made only from what is written here.
        </p>
      )}

      {/* attachments */}
      <div className="rounded-lg border border-dashed border-line-soft bg-bg/60 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs text-ink-secondary">
            Attach lists or documents you already have (e.g. the list of schools, a guest list, the programme order).
            <span className="block">{DOC_FORMATS}. Any size.</span>
          </div>
          <input ref={fileRef} type="file" multiple accept={DOC_ACCEPT} className="hidden" onChange={(e) => pick(e.target.files)} />
          <Button size="small" icon={<AttachmentIcon className="size-3.5" />} disabled={loading}
            onClick={() => fileRef.current?.click()}>Attach files</Button>
        </div>
        {(attachments.length > 0 || uploading.length > 0) && (
          <ul className="mt-3 flex flex-col gap-1.5">
            {attachments.map((a) => (
              <li key={a.name} className="flex items-center gap-2 rounded-md border border-line-soft bg-white px-2.5 py-1.5 text-sm">
                <FileXlsxIcon className="size-4 shrink-0 text-ink-secondary" />
                <span className="min-w-0 flex-1 truncate text-ink">{a.name}</span>
                <span className="shrink-0 text-xs text-ink-secondary">{a.type.toUpperCase()} · {fmtSize(a.size)}</span>
                {a.extract ? (
                  <Tooltip title="The AI can read this file's contents"><span className="shrink-0 text-xs text-[#15803d]">readable</span></Tooltip>
                ) : (
                  <Tooltip title="Only the file name is shared with the AI; it will still be attached to the engagement">
                    <span className="shrink-0 text-xs text-ink-secondary">name only</span>
                  </Tooltip>
                )}
                <button type="button" disabled={loading} onClick={() => onDetach(a.name)}
                  className="flex size-6 shrink-0 items-center justify-center rounded text-[#9ca3af] hover:bg-[#FEF2F2] hover:text-[#DC2626]">
                  <CloseIcon className="size-3.5" />
                </button>
              </li>
            ))}
            {uploading.map((n) => (
              <li key={n} className="flex items-center gap-2 rounded-md border border-line-soft bg-white px-2.5 py-1.5 text-sm text-ink-secondary">
                <span className="min-w-0 flex-1 truncate">{n}</span><span className="text-xs">uploading…</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 pt-1">
        <span className="text-xs text-ink-secondary">
          {mode === 'plan' ? `Engagement: ${engagementName}` : `Existing plan of: ${engagementName}`}
        </span>
        <Button type="primary" loading={loading} disabled={!ok || uploading.length > 0}
          onClick={() => onContinue(text.trim())}>{c.button}</Button>
      </div>
    </div>
  );
}
